import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type LoginInput, loginSchema } from '@/lib/schemas';
import { supabase } from '@/lib/supabase/client';

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      if (error) throw error;
      toast.success('Sesión iniciada');
      await navigate({ to: '/' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-ink">Iniciar sesión</h1>
        <p className="mt-1 text-sm text-quiet">Accede a tu workspace de MEIDEI.</p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input id="login-email" type="email" autoComplete="email" {...form.register('email')} />
          {form.formState.errors.email ? (
            <span className="text-xs text-critical">{form.formState.errors.email.message}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-password">Contraseña</Label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            {...form.register('password')}
          />
          {form.formState.errors.password ? (
            <span className="text-xs text-critical">{form.formState.errors.password.message}</span>
          ) : null}
        </div>

        <Button type="submit" disabled={submitting}>
          {submitting ? 'Iniciando...' : 'Iniciar sesión'}
        </Button>
      </form>

      <p className="text-center text-sm text-quiet">
        Plataforma por invitación. Si olvidaste tu contraseña o necesitas acceso, contacta al
        administrador del sistema.
      </p>
    </div>
  );
}
